import type { FC, ReactNode } from 'react';
import { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Drawer, Stack, Typography, ButtonBase, Popover, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { RouterLink } from 'src/components/router-link';
import { Scrollbar } from 'src/components/scrollbar';
import { usePathname } from 'src/hooks/use-pathname';
import { paths } from 'src/paths';
import type { NavColor } from 'src/types/settings';
import { useCompanySettings } from 'src/hooks/use-company-settings';
import type { Section } from '../config';

const MOBILE_NAV_WIDTH: number = 80;

const useCssVars = (color: NavColor): Record<string, string> => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return useMemo(
    (): Record<string, string> => {
      if (isDarkMode) {
        return {
          '--nav-bg': '#000000',
          '--nav-color': '#ffffff',
          '--nav-border-color': 'rgba(255, 255, 255, 0.08)',
          '--nav-logo-border': 'rgba(255, 255, 255, 0.1)',
          '--nav-section-title-color': 'rgba(255, 255, 255, 0.5)',
          '--nav-item-color': 'rgba(255, 255, 255, 0.7)',
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.08)',
          '--nav-item-active-bg': 'rgba(41, 112, 255, 0.15)',
          '--nav-item-active-color': '#ffffff',
          '--nav-item-disabled-color': 'rgba(255, 255, 255, 0.3)',
          '--nav-item-icon-color': 'rgba(255, 255, 255, 0.5)',
          '--nav-item-icon-active-color': '#2970FF',
          '--nav-item-icon-inactive-color': '#ffffff',
          '--nav-item-icon-disabled-color': 'rgba(255, 255, 255, 0.2)',
          '--nav-item-chevron-color': 'rgba(255, 255, 255, 0.3)',
          '--nav-scrollbar-color': 'rgba(255, 255, 255, 0.1)',
          '--nav-popover-bg': '#0a0a0a',
          '--nav-popover-border': 'rgba(255, 255, 255, 0.1)',
          '--nav-popover-text': 'rgba(255, 255, 255, 0.85)',
          '--nav-popover-text-active': '#fff',
          '--nav-popover-icon': 'rgba(255, 255, 255, 0.6)',
          '--nav-popover-hover': 'rgba(255, 255, 255, 0.08)',
          '--nav-hover-border': 'rgba(255, 255, 255, 0.1)',
        };
      }
      // Light mode
      return {
        '--nav-bg': '#ffffff',
        '--nav-color': '#1e293b',
        '--nav-border-color': 'rgba(0, 0, 0, 0.08)',
        '--nav-logo-border': 'rgba(0, 0, 0, 0.1)',
        '--nav-section-title-color': 'rgba(0, 0, 0, 0.5)',
        '--nav-item-color': 'rgba(0, 0, 0, 0.6)',
        '--nav-item-hover-bg': 'rgba(0, 0, 0, 0.04)',
        '--nav-item-active-bg': 'rgba(41, 112, 255, 0.12)',
        '--nav-item-active-color': '#1e293b',
        '--nav-item-disabled-color': 'rgba(0, 0, 0, 0.3)',
        '--nav-item-icon-color': 'rgba(0, 0, 0, 0.5)',
        '--nav-item-icon-active-color': '#2970FF',
        '--nav-item-icon-inactive-color': 'rgba(0, 0, 0, 0.7)',
        '--nav-item-icon-disabled-color': 'rgba(0, 0, 0, 0.2)',
        '--nav-item-chevron-color': 'rgba(0, 0, 0, 0.3)',
        '--nav-scrollbar-color': 'rgba(0, 0, 0, 0.1)',
        '--nav-popover-bg': '#ffffff',
        '--nav-popover-border': 'rgba(0, 0, 0, 0.1)',
        '--nav-popover-text': 'rgba(0, 0, 0, 0.7)',
        '--nav-popover-text-active': '#1e293b',
        '--nav-popover-icon': 'rgba(0, 0, 0, 0.5)',
        '--nav-popover-hover': 'rgba(0, 0, 0, 0.04)',
        '--nav-hover-border': 'rgba(0, 0, 0, 0.1)',
      };
    },
    [theme, color, isDarkMode]
  );
};

interface NavIconButtonProps {
  icon: ReactNode;
  title: string;
  active?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  href?: string;
  hasPopover?: boolean;
  onClose?: () => void;
}

const NavIconButton: FC<NavIconButtonProps> = ({
  icon,
  title,
  active,
  onClick,
  href,
  hasPopover,
  onClose
}) => {
  const buttonProps = href && !hasPopover
    ? {
        component: RouterLink,
        href: href,
        onClick: onClose
      }
    : {};

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        alignItems: 'center',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        width: 64,
        height: 64,
        gap: 0.5,
        transition: 'all 0.2s ease',
        border: '1px solid transparent',
        ...(active && {
          background: 'var(--nav-item-active-bg)',
          borderColor: 'rgba(41, 112, 255, 0.3)',
        }),
        '&:hover': {
          backgroundColor: 'var(--nav-item-hover-bg)',
          borderColor: 'var(--nav-hover-border)',
        }
      }}
      {...buttonProps}
    >
      <Box
        component="span"
        sx={{
          alignItems: 'center',
          color: active ? 'var(--nav-item-icon-active-color)' : 'var(--nav-item-icon-inactive-color)',
          display: 'inline-flex',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
          '& svg': {
            fontSize: 22,
          }
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: '9px',
          fontWeight: 500,
          color: active ? 'var(--nav-item-icon-active-color)' : 'var(--nav-item-color)',
          textAlign: 'center',
          lineHeight: 1.2,
          maxWidth: 56,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </Typography>
    </ButtonBase>
  );
};

interface MobileNavProps {
  color?: NavColor;
  onClose?: () => void;
  open?: boolean;
  sections?: Section[];
}

export const MobileNav: FC<MobileNavProps> = (props) => {
  const { color = 'evident', open, onClose, sections = [] } = props;
  const pathname = usePathname();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const cssVars = useCssVars(color);
  const { settings: company } = useCompanySettings();

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(null);
  const [activeSection, setActiveSection] = useState<Section | null>(null);

  const handlePopoverOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>, section: Section) => {
    setPopoverAnchor(event.currentTarget);
    setActiveSection(section);
  }, []);

  const handlePopoverClose = useCallback(() => {
    setPopoverAnchor(null);
    setActiveSection(null);
  }, []);

  const isPopoverOpen = Boolean(popoverAnchor);

  const isSectionActive = useCallback((section: Section): boolean => {
    if (section.singleLink && section.path) {
      return pathname === section.path;
    }
    return section.items.some(item => {
      if (item.path && pathname.startsWith(item.path)) {
        return true;
      }
      return false;
    });
  }, [pathname]);

  return (
    <>
      <Drawer
        anchor="left"
        onClose={onClose}
        open={open}
        PaperProps={{
          sx: {
            ...cssVars,
            background: 'var(--nav-bg)',
            borderRight: 'none',
            color: 'var(--nav-color)',
            width: MOBILE_NAV_WIDTH,
            boxShadow: 'none',
          }
        }}
        variant="temporary"
      >
        <Scrollbar
          sx={{
            height: '100%',
            '& .simplebar-content': {
              height: '100%'
            },
            '& .simplebar-scrollbar:before': {
              background: 'var(--nav-scrollbar-color)',
              borderRadius: '4px',
              width: '4px',
            }
          }}
        >
          <Stack sx={{ height: '100%', alignItems: 'center' }}>
            {/* Logo */}
            <Box
              component={RouterLink}
              href={paths.index}
              onClick={onClose}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 64,
                width: '100%',
                borderBottom: '1px solid var(--nav-border-color)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 44,
                  width: 44,
                  borderRadius: isDarkMode ? '8px' : '50%',
                  backgroundColor: isDarkMode ? 'transparent' : '#000',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src="/assets/logo-main.png"
                  alt="Mystic Media"
                  sx={{
                    height: 32,
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </Box>

            {/* Navigation Icons */}
            <Stack
              component="nav"
              spacing={0.5}
              sx={{
                flexGrow: 1,
                alignItems: 'center',
                py: 2,
                px: 1,
              }}
            >
              {sections.map((section, index) => {
                const isActive = isSectionActive(section);
                const hasItems = section.items && section.items.length > 0;

                // For singleLink sections, render as direct link
                if (section.singleLink && section.path) {
                  return (
                    <NavIconButton
                      key={index}
                      icon={section.icon}
                      title={section.subheader || ''}
                      active={isActive}
                      href={section.path}
                      onClose={onClose}
                    />
                  );
                }

                // For sections with items, show popover on click
                if (hasItems) {
                  return (
                    <NavIconButton
                      key={index}
                      icon={section.icon}
                      title={section.subheader || ''}
                      active={isActive}
                      hasPopover
                      onClick={(e) => handlePopoverOpen(e, section)}
                    />
                  );
                }

                return null;
              })}
            </Stack>
          </Stack>
        </Scrollbar>
      </Drawer>

      {/* Popover for section items */}
      <Popover
        anchorEl={popoverAnchor}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'center',
        }}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'center',
        }}
        onClose={handlePopoverClose}
        open={isPopoverOpen}
        PaperProps={{
          sx: {
            ...cssVars,
            backgroundColor: 'var(--nav-popover-bg)',
            borderRadius: '12px',
            border: '1px solid var(--nav-popover-border)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            minWidth: 200,
            ml: 1,
          }
        }}
      >
        {activeSection && (
          <Box sx={{ py: 1 }}>
            <Typography
              sx={{
                px: 2,
                py: 1,
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--nav-section-title-color)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {activeSection.subheader}
            </Typography>
            <List disablePadding>
              {activeSection.items.map((item, itemIndex) => {
                const isItemActive = item.path ? pathname.startsWith(item.path) : false;
                return (
                  <ListItemButton
                    key={itemIndex}
                    component={RouterLink}
                    href={item.path || '#'}
                    onClick={() => {
                      handlePopoverClose();
                      onClose?.();
                    }}
                    sx={{
                      py: 1,
                      px: 2,
                      borderRadius: '8px',
                      mx: 1,
                      mb: 0.5,
                      ...(isItemActive && {
                        backgroundColor: 'var(--nav-item-active-bg)',
                      }),
                      '&:hover': {
                        backgroundColor: 'var(--nav-popover-hover)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: isItemActive ? 'var(--nav-item-icon-active-color)' : 'var(--nav-popover-icon)',
                        '& svg': {
                          fontSize: 20,
                        }
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: isItemActive ? 600 : 400,
                        color: isItemActive ? 'var(--nav-popover-text-active)' : 'var(--nav-popover-text)',
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        )}
      </Popover>
    </>
  );
};

MobileNav.propTypes = {
  color: PropTypes.oneOf<NavColor>(['blend-in', 'discreet', 'evident']),
  onClose: PropTypes.func,
  open: PropTypes.bool,
  sections: PropTypes.array
};
